import { useState, useCallback, useEffect } from "react";
import { Box, Typography, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import SlideTrack from "../../../Common/SlideTrack";
import { useSlideLock } from "../../../../hooks/useSlideLock";
import { CAROUSEL_DRAG_TO_REORDER } from "../../../../i18n/keysTranslations";

const CarouselGridPreview = ({
  images = [],
  onReorder,
  onRemove,
  onAddImage,
  currentIndex: externalCurrentIndex,
  onIndexChange,
  maxImages = Number.POSITIVE_INFINITY,
  t,
}) => {
  const [internalIndex, setInternalIndex] = useState(0);

  const currentIndex =
    externalCurrentIndex !== undefined ? externalCurrentIndex : internalIndex;

  const setCurrentIndex = useCallback(
    (index) => {
      if (onIndexChange) {
        onIndexChange(index);
      } else {
        setInternalIndex(index);
      }
    },
    [onIndexChange]
  );

  useEffect(() => {
    if (externalCurrentIndex !== undefined) {
      setInternalIndex(externalCurrentIndex);
    }
  }, [externalCurrentIndex]);
  const [draggedItem, setDraggedItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);

  const { isSlideLocked, lockSlide } = useSlideLock();

  const onPrev = useCallback(() => {
    if (!lockSlide()) return;
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images.length, lockSlide]);

  const onNext = useCallback(() => {
    if (!lockSlide()) return;
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [images.length, lockSlide]);

  const onDragStart = useCallback((e, index) => {
    setDraggedItem(index);
    e.dataTransfer.effectAllowed = "move";
  }, []);

  const onDragOver = useCallback(
    (e, index) => {
      e.preventDefault();
      if (index !== draggedItem) {
        setDragOverItem(index);
      }
    },
    [draggedItem]
  );

  const onDrop = useCallback(
    (e, targetIndex) => {
      e.preventDefault();
      if (draggedItem === null || draggedItem === targetIndex) return;

      const newImages = [...images];
      const [removed] = newImages.splice(draggedItem, 1);
      newImages.splice(targetIndex, 0, removed);

      const reorderedImages = newImages.map((img, index) => ({
        ...img,
        order: index + 1,
      }));

      // Adjust currentIndex if the current image was moved
      if (currentIndex === draggedItem) {
        setCurrentIndex(targetIndex);
      } else if (currentIndex === targetIndex) {
        setCurrentIndex(
          draggedItem < targetIndex ? currentIndex - 1 : currentIndex + 1
        );
      }

      onReorder(reorderedImages);
      setDraggedItem(null);
      setDragOverItem(null);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    //eslint-disable-next-line
    [draggedItem, images, onReorder, currentIndex]
  );

  const onDragEnd = useCallback(() => {
    setDraggedItem(null);
    setDragOverItem(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onRemoveItem = useCallback(
    (index) => {
      if (currentIndex >= images.length - 1) {
        setCurrentIndex(Math.max(0, images.length - 2));
      }
      onRemove(index);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    //eslint-disable-next-line
    [onRemove, currentIndex, images.length]
  );

  const showAddButton = images.length === 0 || images.length < maxImages;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: 3,
        width: "100%",
        height: "100%",
        maxWidth: "600px",
        margin: "0 auto",
        p: 2,
      }}
    >
      {/* Main large view */}
      <Box
        sx={{
          position: "relative",
          aspectRatio: "1/1",
          borderRadius: 3,
          overflow: "hidden",
          bgcolor: "grey.200",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        {showAddButton && images.length === 0 ? (
          <Box
            onClick={onAddImage}
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              border: "2px dashed",
              borderColor: "grey.400",
              borderRadius: 3,
              "&:hover": {
                borderColor: "primary.main",
                bgcolor: "action.hover",
              },
            }}
          >
            <AddIcon sx={{ fontSize: 48, color: "grey.500" }} />
          </Box>
        ) : images[currentIndex] ? (
          <SlideTrack
            urls={images.map((img) => img?.url)}
            index={currentIndex}
            sx={{ width: "100%", height: "100%" }}
            renderSlide={(slideUrl, slideIndex) => (
              <Box
                component="img"
                src={slideUrl}
                alt={`Imagen ${slideIndex + 1}`}
                sx={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover",
                }}
              />
            )}
          />
        ) : (
          <Box
            onClick={onAddImage}
            sx={{
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              border: "2px dashed",
              borderColor: "grey.400",
              borderRadius: 3,
              "&:hover": {
                borderColor: "primary.main",
                bgcolor: "action.hover",
              },
            }}
          >
            <AddIcon sx={{ fontSize: 48, color: "grey.500" }} />
          </Box>
        )}

        {/* Navigation controls */}
        {images.length > 1 && (
          <>
            <IconButton
              onClick={onPrev}
              disabled={isSlideLocked}
              sx={{
                position: "absolute",
                left: 8,
                top: "50%",
                transform: "translateY(-50%)",
                bgcolor: "rgba(0, 0, 0, 0.7)",
                color: "white",
                "&:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.9)",
                },
              }}
            >
              <ChevronLeftIcon sx={{ color: "#fff !important" }} />
            </IconButton>
            <IconButton
              onClick={onNext}
              disabled={isSlideLocked}
              sx={{
                position: "absolute",
                right: 8,
                top: "50%",
                transform: "translateY(-50%)",
                bgcolor: "rgba(0, 0, 0, 0.7)",
                color: "white",
                "&:hover": {
                  bgcolor: "rgba(0, 0, 0, 0.9)",
                },
              }}
            >
              <ChevronRightIcon sx={{ color: "#fff !important" }} />
            </IconButton>
          </>
        )}

        {/* Position indicator */}
        {images.length > 0 && (
          <Box
            sx={{
              position: "absolute",
              bottom: 12,
              left: "50%",
              transform: "translateX(-50%)",
              bgcolor: "rgba(0, 0, 0, 0.7)",
              px: 2,
              py: 0.5,
              borderRadius: 2,
              fontSize: "14px",
              fontWeight: 500,
              color: "#fff !important",
            }}
          >
            {currentIndex + 1} / {images.length}
          </Box>
        )}
      </Box>

      {/* Draggable thumbnails */}
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
        <Typography variant="caption" sx={{ color: "text.secondary" }}>
          {t(CAROUSEL_DRAG_TO_REORDER)}
        </Typography>

        <Box
          sx={{
            display: "flex",
            gap: 1,
            overflowX: "auto",
            pb: 1,
            "&::-webkit-scrollbar": {
              height: "6px",
            },
            "&::-webkit-scrollbar-thumb": {
              bgcolor: "grey.400",
              borderRadius: "3px",
            },
          }}
        >
          {images.map((image, index) => {
            const isEmptySlot = !image.url;
            return (
              <Box
                key={image.url || index}
                draggable
                onDragStart={(e) => onDragStart(e, index)}
                onDragOver={(e) => onDragOver(e, index)}
                onDrop={(e) => onDrop(e, index)}
                onDragEnd={onDragEnd}
                onClick={() => setCurrentIndex(index)}
                sx={{
                  position: "relative",
                  flexShrink: 0,
                  width: 64,
                  height: 64,
                  borderRadius: 2,
                  overflow: "hidden",
                  cursor: "grab",
                  border: "2px solid",
                  borderColor:
                    currentIndex === index
                      ? "primary.main"
                      : dragOverItem === index
                        ? "primary.main"
                        : "transparent",
                  opacity: draggedItem === index ? 0.5 : 1,
                  transform:
                    draggedItem === index
                      ? "scale(0.95)"
                      : dragOverItem === index
                        ? "scale(1.1)"
                        : "scale(1)",
                  transition: "all 0.2s ease",
                  boxShadow:
                    currentIndex === index
                      ? "0 0 0 3px rgba(255, 152, 0, 0.3)"
                      : "none",
                  "&:active": {
                    cursor: "grabbing",
                  },
                  "&:hover .remove-btn": {
                    opacity: 1,
                  },
                }}
              >
                {isEmptySlot ? (
                  <Box
                    sx={{
                      width: "100%",
                      height: "100%",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "grey.100",
                      border: "2px dashed",
                      borderColor: "grey.300",
                    }}
                  >
                    <AddIcon sx={{ fontSize: 24, color: "grey.400" }} />
                  </Box>
                ) : (
                  <Box
                    component="img"
                    src={image.url}
                    alt={`Thumbnail ${index + 1}`}
                    draggable={false}
                    sx={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                )}

                {/* Order number */}
                <Box
                  sx={{
                    position: "absolute",
                    top: 2,
                    left: 2,
                    bgcolor: "rgba(0, 0, 0, 0.8)",
                    color: "#fff !important",
                    borderRadius: "50%",
                    width: 20,
                    height: 20,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: "11px",
                    fontWeight: 700,
                  }}
                >
                  {index + 1}
                </Box>

                {/* Delete button */}
                <IconButton
                  className="remove-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    onRemoveItem(index);
                  }}
                  size="small"
                  sx={{
                    position: "absolute",
                    top: 2,
                    right: 2,
                    bgcolor: "#ffffff !important",
                    color: "#000000 !important",
                    width: 20,
                    height: 20,
                    minWidth: 20,
                    padding: 0,
                    opacity: 0,
                    transition: "opacity 0.2s ease",
                    "&:hover": {
                      bgcolor: "#f5f5f5 !important",
                      opacity: 1,
                    },
                  }}
                >
                  <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
              </Box>
            );
          })}

          {showAddButton ? (
            <Box
              onClick={onAddImage}
              sx={{
                position: "relative",
                flexShrink: 0,
                width: 64,
                height: 64,
                borderRadius: 2,
                overflow: "hidden",
                cursor: "pointer",
                border: "2px dashed",
                borderColor: "grey.400",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "grey.50",
                "&:hover": {
                  borderColor: "primary.main",
                  bgcolor: "action.hover",
                },
              }}
            >
              <AddIcon sx={{ fontSize: 32, color: "grey.500" }} />
            </Box>
          ) : null}
        </Box>
      </Box>
    </Box>
  );
};

export default CarouselGridPreview;
